import { getHomeRouteForLoggedInUser } from './Utils'

const RETURN_URL_KEY = 'ntts_return_url'

const isAuthPath = (url) => {
  if (!url || typeof url !== 'string') return true
  const path = url.split('?')[0].split('#')[0]
  return (
    path === '/' ||
    path === '/login' ||
    path === '/loginwithotp' ||
    path === '/onboard' ||
    path === '/forgot-password' ||
    path.startsWith('/redirect')
  )
}

/**
 * Saves the user's current destination URL (pathname + search + hash).
 * Stores in both sessionStorage (for tab session) and localStorage (for browser close/reopen).
 */
export const saveReturnUrl = (customUrl = null) => {
  try {
    let target = customUrl

    if (!target && typeof window !== 'undefined') {
      const { pathname, search, hash } = window.location
      if (!isAuthPath(pathname)) {
        target = `${pathname}${search || ''}${hash || ''}`
      }
    }

    if (target && !isAuthPath(target)) {
      sessionStorage.setItem(RETURN_URL_KEY, target)
      localStorage.setItem('redirectBackUrl', target)
    }
  } catch (err) {
    console.warn('Failed to save return URL:', err)
  }
}

/**
 * Retrieves the return URL from location state, sessionStorage, or localStorage.
 */
export const getReturnUrl = (locationState = null) => {
  try {
    // 1. Check React Router location state
    if (locationState?.from) {
      if (typeof locationState.from === 'string') {
        if (!isAuthPath(locationState.from)) {
          return locationState.from
        }
      } else if (locationState.from.pathname) {
        const { pathname, search = '', hash = '' } = locationState.from
        const combined = `${pathname}${search}${hash}`
        if (!isAuthPath(combined)) {
          return combined
        }
      }
    }

    // 2. Check sessionStorage
    const sessionUrl = sessionStorage.getItem(RETURN_URL_KEY)
    if (sessionUrl && !isAuthPath(sessionUrl)) {
      return sessionUrl
    }

    // 3. Check localStorage (persists across browser closes)
    const localUrl = localStorage.getItem('redirectBackUrl')
    if (localUrl && !isAuthPath(localUrl)) {
      return localUrl
    }
  } catch (err) {
    console.warn('Failed to read return URL:', err)
  }

  // Fallback to default user home route
  return getHomeRouteForLoggedInUser('admin')
}

/**
 * Clears saved return URLs after successful navigation.
 */
export const clearReturnUrl = () => {
  try {
    sessionStorage.removeItem(RETURN_URL_KEY)
    localStorage.removeItem('redirectBackUrl')
  } catch (err) {
    console.warn('Failed to clear return URL:', err)
  }
}

/**
 * Safely clears only authentication credentials without wiping user preferences or layout settings.
 */
export const clearAuthSession = () => {
  try {
    localStorage.removeItem('userData')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('name')
  } catch (err) {
    console.warn('Failed to clear auth session:', err)
  }
}

/**
 * Validates if the user currently has active, valid credentials.
 */
export const isUserAuthenticated = () => {
  try {
    const user = localStorage.getItem('userData')
    const accessToken = localStorage.getItem('accessToken')

    if (!user || !accessToken) {
      return false
    }

    const parts = accessToken.split('.')
    if (parts.length !== 3) {
      return false
    }

    const payload = parts[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    )
    const decoded = JSON.parse(atob(padded))

    if (!decoded || !decoded.exp) {
      return false
    }

    return decoded.exp > Math.floor(Date.now() / 1000)
  } catch (err) {
    return false
  }
}
